import React from 'react';
import { useTranslation } from 'react-i18next';
import { ItemType } from 'components/Common/FieldCustom/SelectFieldCustom/SelectFieldCustom';
import ProductSelectItem from 'components/ProductPageV2/ProductDetails/ProductSizes/ProductSelectItem/ProductSelectItem';
import {
  ProductKind,
  ProductVariantsSkusType,
} from 'types/ProductPageV2/Products';
import { StockType } from 'types/ProductPageV2/ProductsStocks';
import {
  PRODUCT_CUP_ATTRIBUTE,
  PRODUCT_SIZE_ATTRIBUTE,
} from 'settings/productV2';

export function useProductSize(
  skus: ProductVariantsSkusType,
  sizes: string[],
  stocks: StockType[] | undefined,
  type: ProductKind,
  filter?: FilterType | undefined
): {
  items: ItemType[];
} {
  const { t } = useTranslation();
  // Used only when type === 2
  const stockMatchesRegexList = {
    [PRODUCT_SIZE_ATTRIBUTE]: /(?<result>[A-Za-z]+)$/,
    [PRODUCT_CUP_ATTRIBUTE]: /^(?<result>[0-9]+)/,
  };

  const filteredSkus: string[] | undefined = filterSkus();
  const filteredStocks = stocks && filteredSkus && filterStocks();

  function findSkusBy(key: string, value: string): string[] | undefined {
    return Object.keys(skus).filter((k: string) => {
      return skus[k].Attributes[key].Value === value;
    });
  }

  function filterSkus(): string[] | undefined {
    switch (type) {
      case 1:
        return Object.keys(skus);
      case 2:
        return filter ? findSkusBy(filter.key, filter.value) : undefined;
    }
  }

  /**
   * Return the match data to correspond with sizes hook parameter
   */
  function stockMatches(stockSize: string): string {
    let result = stockSize;

    if (type === 2 && filter) {
      const match = stockSize.match(stockMatchesRegexList[filter.key]);

      result = match.groups?.result || stockSize;
    }

    return result;
  }

  function filterStocks(): SizeType[] | undefined {
    const stockList = stocks || [];
    const filteredStocks: StockType[] = stockList.filter((stock: StockType) => {
      return filteredSkus?.includes(stock.sku);
    });

    const reducedStocks = filteredStocks.reduce((previous, stock) => {
      return { ...previous, [stockMatches(stock.size)]: stock };
    }, {});

    return sizes.map((size: string) => {
      const stock = reducedStocks[size] || {
        state: 0,
        stateLabel: t('UNAVAILABLE'),
      };

      return {
        size: size,
        state: stock.state,
        stateLabel: stock.stateLabel,
      };
    });
  }

  function generateItems(): ItemType[] {
    return filteredStocks
      ? filteredStocks.map((stock: SizeType) => {
          const { size, state, stateLabel } = stock;
          const sizeLabel = type === 1 ? `${t('LABEL_SIZE')} ${size}` : size;
          const item: ItemType = {
            content: (
              <ProductSelectItem
                label={sizeLabel}
                state={state}
                stateLabel={stateLabel}
              />
            ),
            value: size,
            disabled: state === 0,
          };

          return item;
        })
      : sizes.map((size: string) => {
          const sizeLabel = type === 1 ? `${t('LABEL_SIZE')} ${size}` : size;
          return {
            content: <ProductSelectItem label={sizeLabel} />,
            value: size,
          };
        });
  }

  return {
    items: generateItems(),
  };
}

export type FilterType = {
  key: string;
  value: string;
};

type SizeType = Omit<StockType, 'sku'>;
